// A drawn mark for every deck.
//
// Authored here as inline SVG rather than pulled from an icon set: Mynah ships offline, and a
// hand-built set can share one grid so the deck list reads as a designed system instead of
// assorted clip-art. Every mark is drawn on a 24×24 grid at stroke 1.6 with round joins, and uses
// only `currentColor` — solid for the strokes, `color-mix`ed down for the fills — so a single
// colour on the tile drives the whole mark and it survives all 44 themes in both light and dark.
//
// The hue itself comes from a THEME token, never a fixed hex. A hardcoded palette would clash on
// half the themes; naming `--primary`, `--accent`, `--info`, `--success` or `--warning` means each
// theme restates the deck list in its own colours.

/** The tinted fill used under the strokes — light enough to read as shading, not as a blob. */
const WASH = 'color-mix(in srgb, currentColor 30%, transparent)';
const WASH_SOFT = 'color-mix(in srgb, currentColor 17%, transparent)';

/** Which theme token colours a given mark. Chosen so neighbouring tiles in the grid differ. */
export type IconHue = 'primary' | 'accent' | 'info' | 'success' | 'warning' | 'danger';

interface DeckIcon {
  hue: IconHue;
  /** SVG body on a 24×24 grid. */
  art: string;
}

/** N small blocks — an n-word sentence, drawn as n words. Wraps to two rows past five. */
function blocks(n: number): string {
  const perRow = n <= 4 ? n : Math.ceil(n / 2);
  const rows = n <= 4 ? 1 : 2;
  const w = 4;
  const gap = 1.6;
  const h = 4;
  const rowW = perRow * w + (perRow - 1) * gap;
  const totalH = rows * h + (rows - 1) * gap;
  const x0 = (24 - rowW) / 2;
  const y0 = (24 - totalH) / 2;
  let out = '';
  for (let i = 0; i < n; i++) {
    const r = Math.floor(i / perRow);
    const c = i % perRow;
    const countInRow = r === rows - 1 ? n - perRow * r : perRow;
    // Centre a short final row rather than leaving it hanging left.
    const offset = ((perRow - countInRow) * (w + gap)) / 2;
    const x = x0 + c * (w + gap) + (r === rows - 1 ? offset : 0);
    const y = y0 + r * (h + gap);
    out += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w}" height="${h}" rx="1.3" fill="${WASH}" stroke="currentColor" stroke-width="1.2"/>`;
  }
  return out;
}

/** A stack of cards, one layer per order of magnitude. */
function cardStack(layers: number): string {
  let out = '';
  for (let i = layers - 1; i >= 0; i--) {
    const inset = i * 2.1;
    out +=
      `<rect x="${4 + inset}" y="${5 - inset + 2.1 * (layers - 1)}" width="${13}" height="${10}" rx="2.2" ` +
      `fill="${i === 0 ? WASH : WASH_SOFT}" stroke="currentColor" stroke-width="1.5"/>`;
  }
  return out;
}

export const DECK_ICONS: Record<string, DeckIcon> = {
  // ---- words
  // The deck that decides itself: a path with the next step marked, not a stack of cards. It is
  // the only mark here that stands for an ORDER rather than for a kind of content, so it is drawn
  // as a route — stones already crossed behind, the next one lit, the way on dotted.
  'next-up': {
    hue: 'accent',
    art:
      '<path d="M3.5 18.5c3.2 0 3.6-3.4 6.8-3.4s3.6-3.6 6.8-3.6 3.4-2.6 3.4-2.6" ' +
      'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ' +
      'stroke-dasharray="0.1 3.1"/>' +
      '<circle cx="4.4" cy="18.4" r="1.7" fill="' + WASH_SOFT + '" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="10.6" cy="15.2" r="2.4" fill="' + WASH + '" stroke="currentColor" stroke-width="1.7"/>' +
      '<path d="M17.6 11.2 20.8 8l-3.2-3.2M20.4 8h-4.2" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  // THE TWO SYLLABARIES, told apart by the thing that actually tells them apart: hiragana is
  // written in curves and katakana in straight strokes and corners. A learner who has held a brush
  // once knows which is which from across the room, and these marks say it without lettering —
  // a letter drawn at 24px is a smudge, and the story covers already learned that lesson.
  hiragana: {
    hue: 'info',
    art:
      '<path d="M5 9.5c4-2.6 10.4-2.2 12.6.8 2 2.8-.6 7.4-5 8.2-2.6.5-4.6-1-4.2-3.2.5-2.8 4-5.4 7.4-5.6" ' +
      'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
      '<path d="M13.6 3.6v6.2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" opacity="0.55"/>',
  },
  katakana: {
    hue: 'accent',
    art:
      '<path d="M4.5 6.5h13M15.5 6.5 9 19" fill="none" stroke="currentColor" stroke-width="1.9" ' +
      'stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M13.4 12.6 19.5 19" fill="none" stroke="currentColor" stroke-width="1.7" ' +
      'stroke-linecap="round" opacity="0.55"/>',
  },
  'top-100': { hue: 'primary', art: cardStack(1) },
  'top-500': { hue: 'primary', art: cardStack(2) },
  'top-1000': { hue: 'primary', art: cardStack(3) },
  // a verb is an action: a figure mid-stride
  verbs: {
    hue: 'accent',
    art:
      '<circle cx="13.5" cy="5" r="2.2" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M14 9.2 10.6 12l2.2 3 .6 4.4M12.8 15 8.6 18.4M14 9.2l3.6 1.8 1.6 3.2" ' +
      'fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  // a noun is a thing with a name on it: a luggage tag
  nouns: {
    hue: 'info',
    art:
      '<path d="M11.4 4h6.2A2.4 2.4 0 0 1 20 6.4v6.2a2 2 0 0 1-.6 1.4l-5.4 5.4a2 2 0 0 1-2.8 0l-5.6-5.6a2 2 0 0 1 0-2.8L10 5.2A2 2 0 0 1 11.4 4Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<circle cx="15.6" cy="8.4" r="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },

  // 一 二 三 — the Chinese numerals themselves, as three bars
  numbers: {
    hue: 'success',
    art:
      '<rect x="7" y="5.6" width="10" height="2.4" rx="1.2" fill="currentColor"/>' +
      `<rect x="5" y="10.8" width="14" height="2.4" rx="1.2" fill="${WASH}" stroke="currentColor" stroke-width="1.2"/>` +
      `<rect x="3" y="16" width="18" height="2.4" rx="1.2" fill="${WASH}" stroke="currentColor" stroke-width="1.2"/>`,
  },
  // a ruler, ticks and all
  measures: {
    hue: 'info',
    art:
      '<rect x="2.6" y="8" width="18.8" height="8" rx="2.2" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5"/>` +
      '<path d="M6.6 8v3.4M10.4 8v2.2M14.2 8v3.4M18 8v2.2" ' +
      'stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>',
  },

  // two interlocking links — words that join ideas
  linking: {
    hue: 'accent',
    art:
      '<path d="M9.6 8.4 7.2 6a3.6 3.6 0 1 0-5.1 5.1l2.4 2.4" ' +
      'fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
      '<path d="M14.4 15.6 16.8 18a3.6 3.6 0 1 0 5.1-5.1l-2.4-2.4" ' +
      'fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
      `<rect x="7.4" y="7.4" width="9.2" height="9.2" rx="3" transform="rotate(45 12 12)" ` +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5"/>`,
  },

  // ---- sentences by length: the mark counts the words
  'len-2': { hue: 'accent', art: blocks(2) },
  'len-3': { hue: 'accent', art: blocks(3) },
  'len-4': { hue: 'accent', art: blocks(4) },
  'len-5': { hue: 'accent', art: blocks(5) },
  'len-6': { hue: 'accent', art: blocks(6) },
  'len-7': { hue: 'accent', art: blocks(7) },
  'len-8': { hue: 'accent', art: blocks(8) },

  // ---- situations
  // a columned building with a pediment
  // Asking questions. A question mark would do for all ten and would tell the learner nothing, so
  // each mark says what the question is FOR: a place, a person, a time, a count.
  'ask-ma': {
    hue: 'primary',
    art:
      '<path d="M4.6 6.4h14.8v9.2H11l-4.2 3.4v-3.4H4.6z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M9.8 9.4a2.2 2.2 0 1 1 2.4 2.2v1.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<circle cx="12.2" cy="14" r="0.9" fill="currentColor"/>',
  },
  'ask-a-not-a': {
    hue: 'accent',
    art:
      '<path d="M4.4 5.8h7.2v6.4H7.8L5.2 14.4v-2.2H4.4z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<path d="M19.6 10.2h-7.2v6.4h3.8l2.6 2.2v-2.2h0.8z" fill="' + WASH_SOFT + '" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>',
  },
  'ask-what': {
    hue: 'info',
    art:
      '<circle cx="12" cy="12" r="7.6" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M9.6 9.6a2.5 2.5 0 1 1 2.7 2.5v1.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<circle cx="12.3" cy="16" r="1" fill="currentColor"/>',
  },
  'ask-who': {
    hue: 'success',
    art:
      '<circle cx="12" cy="8.4" r="3.4" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M5.4 19.4a6.6 6.6 0 0 1 13.2 0z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>',
  },
  'ask-where': {
    hue: 'danger',
    art:
      '<path d="M12 20.4s6.2-5.6 6.2-9.8a6.2 6.2 0 1 0-12.4 0c0 4.2 6.2 9.8 6.2 9.8z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<circle cx="12" cy="10.4" r="2.3" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  'ask-when': {
    hue: 'warning',
    art:
      '<circle cx="12" cy="12" r="7.6" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M12 7.4V12l3.2 2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  'ask-why': {
    hue: 'warning',
    art:
      '<path d="M8.6 14.4a5 5 0 1 1 6.8 0c-.8.7-1.1 1.3-1.2 2.2H9.8c-.1-.9-.4-1.5-1.2-2.2z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M10 19h4M10.6 21h2.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  'ask-how': {
    hue: 'accent',
    art:
      '<path d="M4.6 18.4c0-3.4 2.6-4.4 5-4.4s4.4-1.2 4.4-3.4-1.8-3.6-4-3.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M12.6 4.4l3 2.6-3 2.6z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<circle cx="4.6" cy="19" r="1.7" fill="currentColor"/>',
  },
  'ask-how-many': {
    hue: 'info',
    art:
      '<path d="M5.4 19.4V13h3.4v6.4zM10.3 19.4V8.6h3.4v10.8zM15.2 19.4V4.6h3.4v14.8z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>',
  },
  'ask-ne': {
    hue: 'primary',
    art:
      '<path d="M4.4 6.2h9.4v5.6H8.2L5.6 13.8v-2h-1.2z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<path d="M19.6 12.4h-9.4V18h5.6l2.6 2v-2h1.2z" fill="' + WASH_SOFT + '" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<circle cx="15.4" cy="15.2" r="0.9" fill="currentColor"/>',
  },
  'topic-school': {
    hue: 'info',
    art:
      '<path d="M12 3.2 21 8H3l9-4.8Z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M5.6 10v7M9.2 10v7M14.8 10v7M18.4 10v7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M3.4 19.6h17.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>',
  },
  'topic-travel': {
    hue: 'primary',
    art:
      '<path d="M12 2.6c1 0 1.7 1.1 1.7 2.6v3.5l6.6 3.8v2l-6.6-2v3.9l2.2 1.6v1.6L12 18.9l-3.9 1.7v-1.6l2.2-1.6v-3.9l-6.6 2v-2l6.6-3.8V5.2c0-1.5.7-2.6 1.7-2.6Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>`,
  },
  'topic-work': {
    hue: 'warning',
    art:
      '<rect x="3" y="7.4" width="18" height="12" rx="2.4" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M9 7.4V5.8A1.8 1.8 0 0 1 10.8 4h2.4A1.8 1.8 0 0 1 15 5.8v1.6" ' +
      'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M3 12.6h18" stroke="currentColor" stroke-width="1.4"/>',
  },
  // a shopfront with a scalloped awning
  'topic-store': {
    hue: 'success',
    art:
      '<path d="M3.6 9.4h16.8v9.8a1.2 1.2 0 0 1-1.2 1.2H4.8a1.2 1.2 0 0 1-1.2-1.2V9.4Z" ' +
      `fill="${WASH_SOFT}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M3 9.4 4.6 4.6h14.8L21 9.4a2.4 2.4 0 0 1-4.5 0 2.4 2.4 0 0 1-4.5 0 2.4 2.4 0 0 1-4.5 0 2.4 2.4 0 0 1-4.5 0Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M9.4 20.4v-4.6h5.2v4.6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>',
  },
  'topic-house': {
    hue: 'warning',
    art:
      '<path d="M4 10.6 12 4l8 6.6v8.2a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 18.8v-8.2Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M9.6 20v-5.4h4.8V20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>',
  },
  // a pan with rising steam
  'topic-kitchen': {
    hue: 'accent',
    art:
      '<path d="M4 12.4h13v3.2a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-3.2Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M17 13.6h2.6a1.4 1.4 0 0 1 0 2.8H17" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M8 9.4c0-1.4 1.4-1.4 1.4-2.8M12.6 9.4c0-1.4 1.4-1.4 1.4-2.8" ' +
      'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  // a bowl and chopsticks — a Chinese place setting, not a western knife and fork
  'topic-restaurant': {
    hue: 'danger',
    art:
      '<path d="M3.6 11.4h11.8a5.9 5.9 0 0 1-5.9 6 5.9 5.9 0 0 1-5.9-6Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M2.6 19.6h13.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M18 4.4 15.4 14M21 5 17.6 14.4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  // a cross in a rounded shield
  'topic-hospital': {
    hue: 'danger',
    art:
      '<path d="M12 3.2 20 6v6.2c0 4.4-3.2 7.2-8 8.6-4.8-1.4-8-4.2-8-8.6V6l8-2.8Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M12 8v6.4M8.8 11.2h6.4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  },
  'topic-dentist': {
    hue: 'info',
    art:
      '<path d="M12 4.4c2-1.4 5.2-1.4 6.6.6 1.5 2.1.6 5-.3 8.2-.6 2.2-.8 5.6-2.4 5.6-1.5 0-1.5-3.6-2.6-4.8-.5-.6-1.6-.6-2.1 0-1.1 1.2-1.1 4.8-2.6 4.8-1.6 0-1.8-3.4-2.4-5.6-.9-3.2-1.8-6.1-.3-8.2 1.4-2 4.1-2 6.1-.6Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>`,
  },
  'topic-friends': {
    hue: 'success',
    art:
      '<circle cx="8.6" cy="8" r="2.9" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="16.4" cy="9.2" r="2.3" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M3.4 19.4c0-2.9 2.3-5 5.2-5s5.2 2.1 5.2 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M16 14.6c2.5 0 4.6 1.9 4.6 4.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  // two adults and a child
  'topic-family': {
    hue: 'warning',
    art:
      '<circle cx="7.4" cy="7.6" r="2.6" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="16.6" cy="7.6" r="2.6" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="12" cy="14.4" r="2" fill="' + WASH_SOFT + '" stroke="currentColor" stroke-width="1.4"/>' +
      '<path d="M2.8 16.6c0-2.5 2-4.4 4.6-4.4M21.2 16.6c0-2.5-2-4.4-4.6-4.4M8.6 20.6c0-1.9 1.5-3.4 3.4-3.4s3.4 1.5 3.4 3.4" ' +
      'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  'topic-relationships': {
    hue: 'danger',
    art:
      '<path d="M12 20.2S3.6 15.4 3.6 9.6a4.4 4.4 0 0 1 8.4-1.8 4.4 4.4 0 0 1 8.4 1.8c0 5.8-8.4 10.6-8.4 10.6Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>`,
  },
  // steaming bowl of rice
  'topic-food': {
    hue: 'success',
    art:
      '<path d="M3.4 12h17.2a8.6 8.6 0 0 1-8.6 7.4A8.6 8.6 0 0 1 3.4 12Z" ' +
      `fill="${WASH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` +
      '<path d="M2.4 19.6h19.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M9 8.6c0-1.6 1.6-1.6 1.6-3.2M14 8.6c0-1.6 1.6-1.6 1.6-3.2" ' +
      'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  'topic-time': {
    hue: 'info',
    art:
      '<circle cx="12" cy="12.4" r="8" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M12 7.6v5l3.2 2" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  // sun behind a cloud
  'topic-weather': {
    hue: 'warning',
    art:
      '<circle cx="9" cy="8.4" r="3.2" fill="' + WASH + '" stroke="currentColor" stroke-width="1.4"/>' +
      '<path d="M9 2.4v1.4M9 13v1.4M3.4 8.4H2M16 8.4h-1.4M5 4.4 4 3.4M13 4.4l1-1" ' +
      'stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      '<path d="M9.4 19.6h8.2a3.2 3.2 0 0 0 .4-6.4 4.6 4.6 0 0 0-8.7-1.1 3.8 3.8 0 0 0 .1 7.5Z" ' +
      `fill="${WASH_SOFT}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>`,
  },
  // a city bus
  'topic-transport': {
    hue: 'primary',
    art:
      '<rect x="4" y="3.6" width="16" height="13.2" rx="3" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M4.4 10.6h15.2" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="8.2" cy="13.8" r="1.2" fill="currentColor"/>' +
      '<circle cx="15.8" cy="13.8" r="1.2" fill="currentColor"/>' +
      '<path d="M7 17v2M17 17v2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  },
};

/** A generic mark, so a deck added later shows something considered rather than an empty hole. */
const FALLBACK: DeckIcon = {
  hue: 'primary',
  art:
    '<rect x="4" y="5" width="16" height="14" rx="2.6" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M8 9.6h8M8 13h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
};

function markOf(icon: DeckIcon): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'deck-mark');
  // Authored in this file, never from data — no untrusted content reaches innerHTML.
  svg.innerHTML = icon.art;
  return svg;
}

export function deckIcon(deckId: string): SVGSVGElement {
  return markOf(DECK_ICONS[deckId] ?? FALLBACK);
}

export function deckHue(deckId: string): IconHue {
  return (DECK_ICONS[deckId] ?? FALLBACK).hue;
}

/**
 * A cover mark for every story, on the same 24×24 grid as the deck marks.
 *
 * Drawn rather than lettered. The first character of the title was the obvious shortcut and it does
 * not work: three of the ten stories begin with 小 and two with 我, so the covers came out as
 * near-duplicates in a grid where the cover is the thing you scan for.
 */
const STORY_ICONS: Record<string, DeckIcon> = {
  kitten: {
    hue: 'warning',
    art:
      '<path d="M6.6 12.4V7.2l3.2 2.5M17.4 12.4V7.2l-3.2 2.5" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M6.6 12.4a5.4 5.4 0 0 0 10.8 0" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="10" cy="13.4" r="1" fill="currentColor"/><circle cx="14" cy="13.4" r="1" fill="currentColor"/>' +
      '<path d="M12 15.6v1.2M4.4 14.6h3M16.6 14.6h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>',
  },
  rain: {
    hue: 'info',
    art:
      '<path d="M8 13.4a3.4 3.4 0 0 1 .3-6.8 4.4 4.4 0 0 1 8.3 1.5 2.7 2.7 0 0 1-.5 5.3z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M9 16.4l-1 3M12.4 16.4l-1 3M15.8 16.4l-1 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  },
  hungry: {
    hue: 'danger',
    art:
      '<path d="M4.6 12.6h14.8a7.4 7.4 0 0 1-14.8 0z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M3.6 20.4h16.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M9.4 8.6c-1-1.2-1-2.4 0-3.6M14.6 8.6c-1-1.2-1-2.4 0-3.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>',
  },
  ball: {
    hue: 'success',
    art:
      '<circle cx="12" cy="12" r="7.4" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M12 4.6v14.8M4.6 12h14.8" stroke="currentColor" stroke-width="1.3"/>' +
      '<path d="M6.8 6.8c3.4 2 6.6 5.2 8.6 8.6M17.2 6.8c-3.4 2-6.6 5.2-8.6 8.6" stroke="currentColor" stroke-width="1.1" opacity="0.55"/>',
  },
  school: {
    hue: 'primary',
    art:
      '<path d="M5.4 10.2h13.2v8.4H5.4z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M9.2 10.2V8a2.8 2.8 0 0 1 5.6 0v2.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M5.4 13.8h13.2" stroke="currentColor" stroke-width="1.3"/>' +
      '<rect x="10.8" y="12.4" width="2.4" height="2.8" rx="0.8" fill="currentColor"/>',
  },
  birthday: {
    hue: 'accent',
    art:
      '<path d="M5 13.4c1.6 0 1.6 1.2 3.2 1.2s1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.4-1.2v5.2H5z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M7.6 13.2v-2.6M12 13.2v-2.6M16.4 13.2v-2.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M7.6 8.4c.9-.9.9-1.7 0-2.4-.9.7-.9 1.5 0 2.4zM12 8.4c.9-.9.9-1.7 0-2.4-.9.7-.9 1.5 0 2.4zM16.4 8.4c.9-.9.9-1.7 0-2.4-.9.7-.9 1.5 0 2.4z" fill="currentColor"/>',
  },
  bird: {
    hue: 'success',
    art:
      '<path d="M4.6 15.2c1.8-1.6 12.8-1.6 14.8 0-.8 2.8-3.8 4.2-7.4 4.2s-6.6-1.4-7.4-4.2z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<circle cx="12" cy="9.6" r="3.4" fill="' + WASH_SOFT + '" stroke="currentColor" stroke-width="1.5"/>' +
      '<circle cx="11" cy="9" r="0.9" fill="currentColor"/>' +
      '<path d="M15.2 10.2l2.4.8-2.4.9z" fill="currentColor" stroke="currentColor" stroke-width="0.9" stroke-linejoin="round"/>',
  },
  market: {
    hue: 'warning',
    art:
      '<path d="M4.6 9.4h14.8l-1.5 9.4H6.1z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M8.8 9.4V7.2a3.2 3.2 0 0 1 6.4 0v2.2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M9.4 12.6v3.2M14.6 12.6v3.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>',
  },
  snow: {
    hue: 'info',
    art:
      '<path d="M12 3.8v16.4M4.9 7.9l14.2 8.2M19.1 7.9L4.9 16.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M9.6 6.2L12 8.6l2.4-2.4M9.6 17.8L12 15.4l2.4 2.4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<circle cx="12" cy="12" r="1.7" fill="' + WASH + '" stroke="currentColor" stroke-width="1.3"/>',
  },
  bedtime: {
    hue: 'primary',
    art:
      '<path d="M17.6 14.2A6.6 6.6 0 0 1 9.2 5.8a7.2 7.2 0 1 0 8.4 8.4z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
      '<path d="M16.4 4.4l.7 1.7 1.7.7-1.7.7-.7 1.7-.7-1.7-1.7-.7 1.7-.7z" fill="currentColor"/>',
  },
};

/** The cover mark for a story, falling back to the generic mark for one added later. */
export function storyIcon(storyId: string): SVGSVGElement {
  return markOf(STORY_ICONS[storyId] ?? FALLBACK);
}

export function storyHue(storyId: string): IconHue {
  return (STORY_ICONS[storyId] ?? FALLBACK).hue;
}

/**
 * The navigation glyphs.
 *
 * Same 24×24 grid and the same weight as the deck marks. Icons rather than words because eight
 * labelled tabs no longer fit a phone — "Pronunciation" alone is wider than four icons — and a bar
 * that scrolls sideways hides half of itself. Every one keeps its word as a tooltip and as its
 * accessible name, so nothing is lost to anyone who cannot guess a picture.
 */
const NAV_ICONS: Record<string, DeckIcon> = {
  home: {
    hue: 'primary',
    art:
      '<path d="M3.4 11.2L12 4l8.6 7.2V19a1.4 1.4 0 0 1-1.4 1.4h-4.4v-6h-5.6v6H4.8A1.4 1.4 0 0 1 3.4 19z" ' +
      'fill="' + WASH + '" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
  },
  decks: {
    hue: 'primary',
    art:
      '<rect x="3.2" y="6.6" width="13" height="11" rx="2.4" fill="' + WASH + '" stroke="currentColor" stroke-width="1.6"/>' +
      '<path d="M7.4 4.4h9.8a3.4 3.4 0 0 1 3.4 3.4v9.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  },
  stories: {
    hue: 'warning',
    art:
      '<path d="M12 7.2C10.6 5.9 8.7 5.4 5.4 5.4v11.4c3.3 0 5.2.5 6.6 1.8 1.4-1.3 3.3-1.8 6.6-1.8V5.4c-3.3 0-5.2.5-6.6 1.8z" ' +
      'fill="' + WASH + '" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
      '<path d="M12 7.2v11.4" stroke="currentColor" stroke-width="1.4"/>',
  },
  ask: {
    hue: 'info',
    art:
      '<circle cx="10.6" cy="10.6" r="6.4" fill="' + WASH + '" stroke="currentColor" stroke-width="1.6"/>' +
      '<path d="M15.4 15.4l4.4 4.4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>' +
      '<path d="M8.8 9a1.9 1.9 0 1 1 2 1.9v1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  drills: {
    hue: 'accent',
    art:
      '<circle cx="12" cy="13.4" r="7" fill="' + WASH + '" stroke="currentColor" stroke-width="1.6"/>' +
      '<path d="M12 9.8v3.6l2.6 1.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M9.6 3.6h4.8M12 3.6v2.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  },
  test: {
    hue: 'danger',
    art:
      '<path d="M4.6 19.4V13h3.6v6.4zM10.2 19.4V8.6h3.6v10.8zM15.8 19.4V4.6h3.6v14.8z" ' +
      'fill="' + WASH + '" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>',
  },
  pronounce: {
    hue: 'success',
    art:
      '<path d="M4.4 9.6h3.4l4.4-3.6v12l-4.4-3.6H4.4z" fill="' + WASH + '" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
      '<path d="M15.4 9.2a4.2 4.2 0 0 1 0 5.6M18 6.8a7.6 7.6 0 0 1 0 10.4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  },
  progress: {
    hue: 'info',
    art:
      '<path d="M4 16.4l5-5.4 3.6 3 6.8-7.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M14.6 6.8h5v5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M4 20.2h16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.5"/>',
  },
};

/** The glyph for a navigation tab. */
export function navIcon(id: string): SVGSVGElement {
  return markOf(NAV_ICONS[id] ?? FALLBACK);
}
